/**
 * Inquiries (Consultas) page - lead capture management
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InquiryList, InquiryConvertModal } from '../components/inquiries';
import {
  inquiriesApi,
  type InquiryWithAssignment,
  type InquiryCountSummary,
  type LeadInquiryStatus,
} from '../services/inquiriesApi';
import { useToast } from '../contexts/ToastContext';
import { RefreshCw, MessageSquare, UserCheck, XCircle, CheckCircle } from 'lucide-react';
import { pageStyles, statStyles } from '../lib/styles';

export function InquiriesPage() {
  const toast = useToast();

  // Data state
  const [inquiries, setInquiries] = useState<InquiryWithAssignment[]>([]);
  const [countSummary, setCountSummary] = useState<InquiryCountSummary | null>(null);
  const [activeTab, setActiveTab] = useState<LeadInquiryStatus | 'all'>('new' as const);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modal state
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryWithAssignment | null>(null);

  // Load count summary
  const loadCountSummary = useCallback(async () => {
    try {
      const summary = await inquiriesApi.getCountSummary();
      setCountSummary(summary);
    } catch {
      // Silent fail for summary
    }
  }, []);

  // Load inquiries
  const loadInquiries = useCallback(async (status?: LeadInquiryStatus | 'all') => {
    setIsLoading(true);
    try {
      const response = await inquiriesApi.list({
        status: status === 'all' ? undefined : status as LeadInquiryStatus | undefined,
        limit: 100,
      });
      // Handle both array and object response formats
      const data = Array.isArray(response) ? response : (response as { inquiries: InquiryWithAssignment[] }).inquiries || [];
      setInquiries(data);
    } catch {
      toast.error('Error al cargar consultas');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    loadCountSummary();
  }, [loadCountSummary]);

  // Load inquiries when tab changes
  useEffect(() => {
    loadInquiries(activeTab === 'all' ? undefined : activeTab);
  }, [activeTab, loadInquiries]);

  // Refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      loadCountSummary(),
      loadInquiries(activeTab === 'all' ? undefined : activeTab),
    ]);
    setIsRefreshing(false);
  };

  // Status change handler
  const handleStatusChange = async (id: string, status: LeadInquiryStatus) => {
    try {
      await inquiriesApi.update(id, { status });
      toast.success('Estado actualizado');
      await handleRefresh();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  // Convert handler
  const handleConvert = (inquiry: InquiryWithAssignment) => {
    setSelectedInquiry(inquiry);
    setConvertModalOpen(true);
  };

  const handleConvertSubmit = async (inquiryId: string, customerId: string) => {
    await inquiriesApi.convert(inquiryId, { customerId });
    toast.success('Consulta convertida a cliente');
    await handleRefresh();
  };

  // Delete handler
  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta consulta?')) return;

    try {
      await inquiriesApi.delete(id);
      toast.success('Consulta eliminada');
      await handleRefresh();
    } catch {
      toast.error('Error al eliminar consulta');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Consultas</h1>
          <p className={pageStyles.subtitle}>
            Consultas del formulario de contacto
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Summary cards */}
      {countSummary && (
        <div className={statStyles.grid}>
          <div className={statStyles.card}>
            <div className={statStyles.header}>
              <span className={statStyles.label}>Nuevas</span>
              <MessageSquare className="h-3.5 w-3.5 text-blue-500" />
            </div>
            <div className={statStyles.value}>{countSummary.new}</div>
          </div>
          <div className={statStyles.card}>
            <div className={statStyles.header}>
              <span className={statStyles.label}>Contactadas</span>
              <UserCheck className="h-3.5 w-3.5 text-yellow-500" />
            </div>
            <div className={statStyles.value}>{countSummary.contacted}</div>
          </div>
          <div className={statStyles.card}>
            <div className={statStyles.header}>
              <span className={statStyles.label}>Convertidas</span>
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            </div>
            <div className={statStyles.value}>{countSummary.converted}</div>
          </div>
          <div className={statStyles.card}>
            <div className={statStyles.header}>
              <span className={statStyles.label}>Descartadas</span>
              <XCircle className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <div className={statStyles.value}>{countSummary.dismissed}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as LeadInquiryStatus | 'all')}>
        <TabsList className="h-8">
          <TabsTrigger value="new" className="text-xs px-3 py-1">
            Nuevas {countSummary && countSummary.new > 0 && `(${countSummary.new})`}
          </TabsTrigger>
          <TabsTrigger value="contacted" className="text-xs px-3 py-1">Contactadas</TabsTrigger>
          <TabsTrigger value="converted" className="text-xs px-3 py-1">Convertidas</TabsTrigger>
          <TabsTrigger value="dismissed" className="text-xs px-3 py-1">Descartadas</TabsTrigger>
          <TabsTrigger value="all" className="text-xs px-3 py-1">Todas</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-3">
          <InquiryList
            inquiries={inquiries}
            isLoading={isLoading}
            onStatusChange={handleStatusChange}
            onConvert={handleConvert}
            onDelete={handleDelete}
          />
        </TabsContent>
      </Tabs>

      {/* Convert modal */}
      <InquiryConvertModal
        inquiry={selectedInquiry}
        isOpen={convertModalOpen}
        onClose={() => {
          setConvertModalOpen(false);
          setSelectedInquiry(null);
        }}
        onConvert={handleConvertSubmit}
      />
    </div>
  );
}
